import { useEffect, useId, useRef, useState } from 'react';

import type { Order } from '../../../types/@types/order';
import { Modal, Button, Input } from '../../../components/ui/primitives';

interface ConfirmCancelOrderModalProps {
  open: boolean;
  order: Pick<Order, 'id' | 'customerName' | 'table' | 'orderSequence' | 'destination'> | null;
  loading?: boolean;
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}

function getOrderLabel(
  order: ConfirmCancelOrderModalProps['order'],
): string {
  if (!order) return 'pedido';
  if (order.customerName?.trim()) return order.customerName.trim();
  if (order.table && typeof order.table === 'object' && 'name' in order.table && order.table.name) {
    return String(order.table.name);
  }
  if (order.table && typeof order.table === 'object' && 'number' in order.table && order.table.number) {
    return `Mesa ${order.table.number}`;
  }
  if (typeof order.orderSequence === 'number') {
    return `Orden #${order.orderSequence}`;
  }
  return `Pedido #${String(order?.id).slice(-6) ?? ''}`;
}

export function ConfirmCancelOrderModal({
  open,
  order,
  loading,
  onConfirm,
  onClose,
}: ConfirmCancelOrderModalProps) {
  const titleId = useId();
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      confirmRef.current?.focus();
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(reason.trim() ? reason.trim() : undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      titleId={titleId}
      role="dialog"
      aria-labelledby={titleId}
      className="max-w-lg"
    >
      <div className="space-y-2 border-b border-border px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Confirmar cancelación</p>
        <h2 id={titleId} className="text-lg font-semibold text-text">
          ¿Cancelar el pedido de {getOrderLabel(order)}?
        </h2>
        <p className="text-sm text-text-muted">
          Esta acción eliminará el pedido y no podrá deshacerse.
        </p>
      </div>
      <div className="space-y-3 px-6 py-5">
        <label className="block text-sm font-semibold text-text" htmlFor={`${titleId}-reason`}>
          Motivo (opcional)
        </label>
        <Input
          id={`${titleId}-reason`}
          placeholder="Ej: Cliente se retiró"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
        <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
          Volver
        </Button>
        <Button
          type="button"
          ref={confirmRef}
          onClick={handleConfirm}
          disabled={loading}
          isLoading={loading}
          variant="destructive"
        >
          Cancelar pedido
        </Button>
      </div>
    </Modal>
  );
}

export default ConfirmCancelOrderModal;
